import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { Observable, Subject, map, takeUntil, switchMap } from 'rxjs';

import { LanguageService } from '@fcom/ui-translate';
import { ButtonType, LoaderTheme, NotificationTheme } from '@fcom/ui-components';
import { FormStatus, pnrFormatValidator } from '@fcom/service-forms';

import { PaxTripService } from '../../services';
import { AddFrequentFlyerResponse, AddFrequentFlyerError } from '../../interfaces/pax-trip.interface';

@Component({
  selector: 'fin-loyalty-add-trips',
  templateUrl: './loyalty-add-trips.component.html',
  styleUrls: ['../../assets/styles/common.scss'],
  standalone: false,
})
export class LoyaltyAddTripsComponent implements OnInit, OnDestroy {
  private formBuilder = inject(UntypedFormBuilder);
  private languageService = inject(LanguageService);
  private paxTripService = inject(PaxTripService);

  readonly FormStatus = FormStatus;
  readonly ButtonType = ButtonType;
  readonly NotificationTheme = NotificationTheme;
  readonly LoaderTheme = LoaderTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  hideError = true;
  validationMessages$: Observable<Record<string, string>>;
  formStatus: FormStatus = FormStatus.INITIAL;
  form: UntypedFormGroup;
  messageLabel: string;

  private ngDestroyed$ = new Subject();

  constructor() {
    this.validationMessages$ = this.languageService
      .translate('SERVICE_FORMS.serviceForms.fields.reservationNumber')
      .pipe(
        map(({ pattern, required }) => ({
          pattern,
          required,
        }))
      );
  }

  ngOnInit() {
    this.form = this.formBuilder.group({
      bookingReference: ['', [pnrFormatValidator(), Validators.required]],
    });
  }

  ngOnDestroy() {
    this.ngDestroyed$.next(true);
  }

  onSubmit() {
    if (this.form.valid) {
      this.formStatus = FormStatus.LOADING;
      this.paxTripService
        .addTrip(this.form.controls.bookingReference.value)
        .pipe(
          switchMap((response: AddFrequentFlyerResponse) =>
            this.paxTripService.verifyAddedTrip(this.form.controls.bookingReference.value).pipe(
              map((tripAdded: boolean) => {
                if (!tripAdded) {
                  throw new Error('Added trip not found on the bookings list');
                }
                return tripAdded && response;
              })
            )
          ),
          takeUntil(this.ngDestroyed$)
        )
        .subscribe({
          next: () => {
            this.formStatus = FormStatus.COMPLETE;
            this.messageLabel = 'myTrips.addTrips.successMessage';
          },
          // eslint-disable-next-line rxjs-x/no-implicit-any-catch
          error: (response: AddFrequentFlyerResponse) => {
            this.formStatus = FormStatus.ERROR;
            let messageLabel = 'myTrips.addTrips.errorMessage';

            if (response?.error?.status) {
              if (
                response.error.status.message === AddFrequentFlyerError.BOOKING_NOT_FOUND ||
                response.error.status.message === AddFrequentFlyerError.PASSENGER_NOT_FOUND
              ) {
                messageLabel = 'myTrips.addTrips.notFoundMessage';
              } else if (response.error.status.message === AddFrequentFlyerError.GROUP_BOOKING) {
                messageLabel = 'myTrips.addTrips.groupBookingMessage';
              }
            }

            this.messageLabel = messageLabel;
          },
        });
    }
  }
}
